import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Searchable model picker.
 *
 * Replaces the native grouped <select> for choosing an agent's model. Same
 * grouping-by-provider as before, but with a type-to-filter search box so a
 * long catalogue is quick to scan. Unavailable models stay visible but are
 * disabled (matching the old `disabled` options). Used for both the primary
 * model and the backup-model selectors on the agent Config tab.
 */
export type PickerModel = {
  id: string;
  modelId: string;
  displayName: string;
  provider: string;
  accessReady: boolean;
  category?: string;
};

export function ModelPicker({
  value,
  onChange,
  modelsByProvider,
  providers,
  providerDisplayNames,
  emptyLabel,
  showProviderStatus = false,
  disabled = false,
  statusSlot,
}: {
  value: string;
  onChange: (modelId: string) => void;
  modelsByProvider: Record<string, PickerModel[]>;
  providers?: Record<string, { ready: boolean; label: string }>;
  providerDisplayNames: Record<string, string>;
  /** Label for the empty/clear row (e.g. "Select a model..." or "— Use platform default —"). */
  emptyLabel: string;
  /** Show the ✅/❌ + access label in group headers (primary selector only). */
  showProviderStatus?: boolean;
  disabled?: boolean;
  /**
   * Optional badge rendered inside the closed field, to the left of the
   * chevron. The agent Config tab uses it for the "Running" state, which used
   * to sit in a separate row above and repeated the model name.
   */
  statusSlot?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Reset + focus the search each time it opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Human label for the currently selected model.
  const selectedLabel = useMemo(() => {
    for (const models of Object.values(modelsByProvider)) {
      const m = models.find((x) => x.modelId === value);
      if (m) return m.displayName;
    }
    return null;
  }, [value, modelsByProvider]);

  const q = query.trim().toLowerCase();
  const filteredGroups = useMemo(
    () =>
      Object.entries(modelsByProvider)
        .map(([provider, models]) => {
          const provName = (providerDisplayNames[provider] || provider).toLowerCase();
          const matched = q
            ? models.filter(
                (m) =>
                  m.displayName.toLowerCase().includes(q) ||
                  m.modelId.toLowerCase().includes(q) ||
                  provName.includes(q),
              )
            : models;
          return [provider, matched] as const;
        })
        .filter(([, models]) => models.length > 0),
    [modelsByProvider, providerDisplayNames, q],
  );

  const select = (modelId: string) => {
    onChange(modelId);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-md border border-border bg-surface px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className={cn("flex-1 truncate text-left", !selectedLabel && "text-text-muted")}>
          {selectedLabel || emptyLabel}
        </span>
        {statusSlot}
        <ChevronDown size={16} className="shrink-0 text-text-muted" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border border-border bg-surface shadow-lg">
          {/* Search box */}
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search size={14} className="shrink-0 text-text-muted" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search models…"
              className="w-full bg-transparent text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
            />
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            {/* Empty / clear row */}
            <button
              type="button"
              onClick={() => select("")}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-surface-hover",
                value === "" ? "text-accent" : "text-text-secondary",
              )}
            >
              <Check size={14} className={cn("shrink-0", value === "" ? "opacity-100" : "opacity-0")} />
              <span className="flex-1 truncate text-left">{emptyLabel}</span>
            </button>

            {filteredGroups.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-text-muted">
                No models match “{query}”.
              </p>
            ) : (
              filteredGroups.map(([provider, models]) => {
                const access = providers?.[provider];
                const icon = showProviderStatus && access ? (access.ready ? "✅ " : "❌ ") : "";
                const suffix = showProviderStatus && access?.label ? ` — ${access.label}` : "";
                return (
                  <div key={provider}>
                    <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                      {icon}
                      {providerDisplayNames[provider] || provider}
                      {suffix}
                    </div>
                    {models.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        disabled={!m.accessReady}
                        onClick={() => select(m.modelId)}
                        className={cn(
                          "flex w-full items-center gap-2 px-3 py-1.5 text-sm",
                          !m.accessReady
                            ? "cursor-not-allowed text-text-muted/60"
                            : "hover:bg-surface-hover",
                          value === m.modelId ? "text-accent" : "text-text-primary",
                        )}
                      >
                        <Check
                          size={14}
                          className={cn("shrink-0", value === m.modelId ? "opacity-100" : "opacity-0")}
                        />
                        <span className="flex-1 truncate text-left">
                          {m.displayName}
                          {!m.accessReady ? " (not available)" : ""}
                          {m.category === "reasoning" ? " [reasoning]" : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
